package dev.deeptelar.foodie

/** The texts in Catalan. */
object Catalan : Texts {
    override val tagline = "Menja on menja la gent del lloc. Ho planifica un agent que funciona al teu navegador."
    override val languageLabel = "Llengua"
    override val settings = "Configuració"
    override val close = "Tanca"
    override val restaurantsFrom = "Restaurants de "
    override val ccBySaAnd = " (CC BY-SA) i dels col·laboradors d'"
    override val contributorsCheckHours = ". Comprova els horaris abans d'anar-hi: una font pot estar desactualitzada."
    override val workflowIsAGraphOf = "El flux de treball és un graf de "
    override val kotlinLibrary = ", una biblioteca de Kotlin per a agents i fluxos d'IA."

    override val whereAreYouGoing = "On vas?"
    override val startHint =
        "Explica-m'ho per escrit o en veu alta: on, quant de temps, amb qui, què t'encanta i què no pots menjar. " +
            "Et pregunto el que falti."
    override val upFrontTitle = "Digues-m'ho ara i no t'ho preguntaré"
    override val upFrontHint = "Tot és opcional: el que et saltis, t'ho preguntaré després si el viatge ho necessita."
    override val requestPlaceholder = "3 dies a Lisboa i 2 a Porto. Res de porc, i m'encanten els dolços."
    override val examples =
        listOf(
            "3 dies a Lisboa i 2 a Porto. Res de porc, i m'encanten els dolços.",
            "Un cap de setmana a Nàpols: pizza i menjar de carrer amb poc pressupost.",
            "4 dies a Oaxaca, vegetarià.",
            "Dos dies a Bolonya i un a Mòdena. Vull els clàssics.",
        )
    override val planMyRoute = "Planifica la meva ruta"
    override val trySample = "Prova l'exemple, sense clau"
    override val speechPrivacy =
        "Quan parles, el navegador converteix la teva veu en text. La majoria ho fan als servidors del seu fabricant, així que la gravació hi va; aquesta pàgina només rep el text."
    override val needAKey = "Per planificar un viatge teu, afegeix la clau d'un model a Configuració. L'exemple planifica dos dies a Lisboa amb un guió en lloc del model."
    override val howItWorks = "Com funciona"
    override val howItWorksSteps =
        listOf(
            "Un model llegeix la teva petició i et pregunta el que et preguntaria un planificador gastronòmic: al·lèrgies, pressupost, qui ve, fins on t'atreveixes a tastar.",
            "Troba les teves parades al mapa. Si no pot, t'ho pregunta.",
            "Es consulten alhora una guia de viatge, un mapa de carrers i, amb una clau de Google, les ressenyes.",
            "El model et diu els plats típics de cada lloc.",
            "Un agent tria un restaurant per a cada àpat. Pot tornar a buscar a les fonts un plat concret.",
            "Reps la ruta en un mapa i pots demanar canvis fins que t'agradi.",
        )

    override val keysStayHere = "Les claus es desen només en aquest navegador i no s'envien a ningú més que al seu proveïdor."
    override val visitsCounted =
        "La pàgina compta les visites i alguns passos, com un pla començat o una ruta llesta, amb GoatCounter: sense galetes i sense res del viatge."
    override val model = "Model"
    override val openAiProvider = "OpenAI, Gemini, Ollama o un altre servidor amb l'API d'OpenAI"
    override val anthropicKey = "Clau de l'API d'Anthropic"
    override val claudeModel = "Model de Claude"
    override val apiAddress = "Adreça de l'API"
    override val apiKey = "Clau de l'API"
    override val emptyForLocalServer = "Deixa-la buida per a un servidor local"
    override val localServerHint =
        "Un servidor d'aquest ordinador, com Ollama, ha de permetre el lloc d'aquesta pàgina, o el navegador n'atura les respostes. El navegador també pot preguntar si la pàgina pot accedir a la xarxa local. Inicia Ollama amb:"
    override val googleKey = "Clau de Google Places, per a les valoracions (opcional)"
    override val googleBills =
        "Google dona una quantitat de cerques gratis cada mes i cobra la resta al titular de la clau. " +
            "Un viatge fa una cerca per parada, i unes quantes més quan el planificador busca un lloc. " +
            "Sense clau, el planificador fa servir la guia de viatge i el mapa de carrers."
    override val howToGetAGoogleKey = "Com aconseguir una clau de Google"

    override fun googleKeySteps(site: String) =
        listOf(
            KeyStep("Crea un projecte a la consola de Google Cloud. Google demana un compte de facturació, amb una targeta.", "Crea un projecte", GoogleConsole.NEW_PROJECT),
            KeyStep("Obre Places API (New) i prem Habilita.", "Places API (New)", GoogleConsole.PLACES_API),
            KeyStep("A Credencials, prem Crea credencials i després Clau d'API.", "Credencials", GoogleConsole.CREDENTIALS),
            KeyStep(
                "Restringeix la clau, perquè només aquesta pàgina la pugui fer servir: a Restriccions d'aplicació tria Llocs web i afegeix $site, " +
                    "i a Restriccions d'API tria Places API (New).",
            ),
            KeyStep("Copia la clau i enganxa-la a dalt."),
        )
    override val googlePrices = "Els preus de Google"
    override val googleGuide = "La guia de Google"
    override val done = "Fet"

    override val planningYourRoute = "Planificant la teva ruta"
    override val stepInterview = "Coneixent el teu viatge"
    override val stepBrief = "Buscant les teves parades al mapa"
    override val stepGuide = "Guia de viatge · Wikivoyage"
    override val stepMap = "Mapa de carrers · OpenStreetMap"
    override val stepReviews = "Ressenyes · Google"
    override val stepDishes = "Els plats típics de cada lloc"
    override val stepPlan = "Triant on menjar"
    override val stepOrder = "Posant els dies en ordre"
    override val reviewsNotUsed = "No s'utilitza. Afegeix una clau de Google a Configuració per veure valoracions."
    override val sampleRunning = "Això és l'exemple: un guió fa de model. El que responen les fonts és real: la guia de viatge i el mapa, consultats una vegada i desats."
    override val cancel = "Cancel·la"

    override fun places(count: Int) = if (count == 1) "1 lloc" else "$count llocs"

    override fun daysIn(days: Int, place: String) = "$days ${if (days == 1) "dia" else "dies"} a $place"

    override fun mealsOnTheRoute(count: Int) = "$count ${if (count == 1) "àpat" else "àpats"} a la ruta"

    override fun retrying(problem: String) = "No ha funcionat ($problem). Ho torno a provar."

    override fun didSetMeal(day: String, meal: String, restaurant: String) = "Dia $day, $meal: $restaurant"

    override fun didRemoveMeal(day: String, meal: String) = "He tret de la ruta: $meal del dia $day"

    override fun didSearchMap(stop: String, what: String) = "He buscat $what al mapa de $stop"

    override fun didReadGuide(page: String) = "He llegit la guia de viatge de $page"

    override fun didSearchReviews(query: String) = "He buscat «$query» a les ressenyes"

    override fun didLookThrough(stop: String, words: String) = "He repassat els llocs de $stop" + if (words.isBlank()) "" else " buscant $words"

    override val stoppedHalfway = "El teu pla s'ha quedat a mitges"
    override val pause = "Fes una pausa"
    override val paused = "En pausa"
    override val pausedHint = "Afegeix el que el planificador hauria de saber, o digues-li que faci alguna cosa d'una altra manera. Continuarà tenint-ho en compte."
    override val asidePlaceholder = "Per exemple: tindrem cotxe. O: més marisc i menys pasta."
    override val continueRun = "Continua"
    override val startOver = "Torna a començar"
    override val gotStuck = "El planificador s'ha encallat"
    override val foundIsSaved = "El que havia trobat abans està desat. Pots tornar a provar el pas."
    override val tryAgain = "Torna-ho a provar"
    override val somethingWentWrong = "Alguna cosa ha anat malament."

    override val refusedLooksUnreachable =
        "Si l'adreça és correcta, el més probable és que el proveïdor hagi rebutjat la petició: els navegadors n'amaguen el motiu. Revisa a Configuració la clau, el nom del model i el saldo del compte."

    override fun closedWhileWorking(request: String) = "La pàgina es va tancar mentre el planificador treballava en «$request». Tot el que havia trobat està desat."

    override val oneQuestion = "Una pregunta"
    override val aFewQuestions = "Unes quantes preguntes"
    override val oneMoreThing = "Una cosa més abans de planificar."
    override val lastQuestions = "Són les últimes. Toca tot el que encaixi, escriu el que no o salta una pregunta."
    override val continueAnswers = "Continua"
    override val planNow = "Prou preguntes"
    override val sampleAsks = "Això és l'exemple: un guió fa les preguntes i no llegeix les respostes."
    override val allergyNote =
        "El planificador deixa fora el que diguis i t'indica què has de dir al personal. No pot saber què posa una cuina en un plat: digues-ho sempre al cambrer."
    override val pregnancyNote = "Amb un embaràs, el planificador segueix els consells habituals: res de peix, carn o ou crus, formatges sense pasteuritzar, embotits ni alcohol. El que digui el teu metge va primer."
    override val yourAnswer = "La teva resposta"
    override val somethingElse = "Una altra cosa, o més detalls"

    override fun youAlreadyToldMe(topics: List<String>) = "Ja m'has dit: ${topics.joinedWith("i")}."

    override fun moreQuestions(count: Int) = "En queden unes $count després d'aquestes. Toca tot el que encaixi, escriu el que no o salta una pregunta."

    override fun topic(topic: Topic) =
        when (topic) {
            Topic.WHERE -> "on i quant de temps"
            Topic.ALLERGIES -> "al·lèrgies"
            Topic.PREGNANCY -> "embaràs"
            Topic.DIET -> "dieta"
            Topic.DATES -> "quan"
            Topic.PARTY -> "qui ve"
            Topic.STAY -> "on t'allotges"
            Topic.MEALS -> "quins àpats"
            Topic.BUDGET -> "pressupost"
            Topic.TASTE -> "gustos"
            Topic.DRINKS -> "begudes"
        }

    override fun question(topic: Topic) =
        when (topic) {
            Topic.WHERE -> Question(topic, "On vas i quants dies estaràs a cada lloc?")
            Topic.ALLERGIES ->
                Question(topic, "Tens alguna al·lèrgia alimentària? Si en tens, has d'evitar fins i tot les traces?", listOf("Cap", "Fruits secs", "Marisc", "Gluten", "Lactis"), alone = listOf("Cap"))
            Topic.PREGNANCY -> Question(topic, "Hi ha algú embarassada en aquest viatge?", listOf("No", "Sí"), alone = listOf("No", "Sí"))
            Topic.DIET -> Question(topic, "Hi ha res que no mengis?", listOf("Menjo de tot", "Vegetarià", "Piscivegetarià", "Halal", "Sense porc"), alone = listOf("Menjo de tot"))
            Topic.DATES -> Question(topic, "Quan hi vas?", listOf("Primavera", "Estiu", "Tardor", "Hivern"))
            Topic.PARTY -> Question(topic, "Amb qui menges?", listOf("Només jo", "Dues persones", "Família amb nens", "Grup d'amics"), alone = listOf("Només jo"))
            Topic.STAY -> Question(topic, "On t'allotges a cada lloc, i des de quin dia si canvies d'hotel? N'hi ha prou amb el barri o el nom de l'hotel. Digues-me també si vols algun àpat a prop, com l'esmorzar.")
            Topic.MEALS -> Question(topic, "Quins àpats ha de planificar la ruta?", listOf("Esmorzar", "Dinar", "Berenar", "Sopar"))
            Topic.BUDGET ->
                Question(topic, "Quant vols gastar per àpat?", listOf("Com menys millor", "Preu mitjà", "Preu mitjà i un sopar especial", "Els diners no importen"), alone = listOf("Com menys millor", "Preu mitjà", "Preu mitjà i un sopar especial", "Els diners no importen"))
            Topic.TASTE -> Question(topic, "Fins on t'atreveixes a tastar, i hi ha res que no t'agradi?", listOf("Els clàssics", "Tinc curiositat", "El que mengi la gent del lloc"), alone = listOf("Els clàssics", "Tinc curiositat", "El que mengi la gent del lloc"))
            Topic.DRINKS -> Question(topic, "Què vols beure a la ruta?", listOf("Vi", "Cervesa", "Cafè", "Còctels", "Sense alcohol"))
        }

    override val planTab = "Pla"
    override val detailsTab = "Les teves dades"
    override val skipped = "Sense resposta"
    override val youEditedDetails = "Has canviat les teves dades."
    override val youErasedDetails = "Has esborrat les teves dades."
    override val shallIPlan = "Planifico la ruta?"
    override val yesPlanIt = "Sí, planifica-la"
    override val send = "Envia"
    override val seeYourDetails = "Mira o canvia les teves dades"
    override val noDetailsYet = "Encara res. Aquí apareix el que expliquis al planificador sobre tu."
    override val detailsWhileWorking = "El planificador hi està treballant. Posa'l en pausa per canviar-ho."
    override val detailsReadOnly = "La ruta s'ha planificat per a això. «Torna a començar» ho esborra."
    override val save = "Desa"
    override val saved = "Desat."

    override val hereIsWhatIHave = "Això és el que tinc"
    override val profileHint = "Per a això es planifica la ruta. Canvia o esborra el que vulguis i desa-ho."
    override val correctionPlaceholder = "Alguna cosa a corregir? Per exemple: som tres, no dos."
    override val correctIt = "Corregeix"
    override val sampleDidNotRead = "Això és l'exemple: el guió no ha llegit les teves respostes."
    override val allergies = "Al·lèrgies"
    override val noAllergies = "Cap"
    override val pregnancy = "Embaràs"
    override val someoneIsPregnant = "Hi ha algú embarassada"
    override val diet = "Dieta"
    override val eatsEverything = "Menja de tot"
    override val whenLabel = "Quan"
    override val who = "Qui"
    override val meals = "Àpats"
    override val budget = "Pressupost"
    override val withASpecialMeal = ", amb un àpat especial"
    override val taste = "Gustos"
    override val drinks = "Begudes"
    override val wishes = "Desitjos"

    override fun evenTraces(food: String) = "$food, fins i tot traces"

    override val notSaid = "No ho has dit"
    override val erase = "Esborra"
    override val addAllergy = "Afegeix una al·lèrgia"
    override val allergyPlaceholder = "Per exemple, cacauets"
    override val evenTracesOption = "Fins i tot traces"
    override val oneSpecialMeal = "Un àpat especial"
    override val mealsIfNone = "Si no en tries cap: dinar i sopar."
    override val eraseEverything = "Esborra-ho tot"
    override val keptInThisBrowser = "Només es desa en aquest navegador, amb aquest viatge. «Torna a començar» ho esborra tot."
    override val updateForTheseDetails = "Actualitza la ruta amb aquestes dades"

    override fun budget(word: String) =
        when (word.trim().lowercase()) {
            "low" -> "baix"
            "medium" -> "mitjà"
            "high" -> "alt"
            else -> word
        }

    override val noMealPlanned = "El planificador no ha posat cap àpat a la ruta. Digues-li a sota què busques, o torna a començar."
    override val wholeTrip = "Tot el viatge"
    override val nothingPlannedThisDay = "No hi ha res planificat per a aquest dia."
    override val plannedForYou = "Planificat per a tu"
    override val couldNotBeRead = "El que no s'ha pogut llegir"
    override val enjoyYourTrip = "Gaudeix del viatge"
    override val printOrSave = "Imprimeix o desa com a PDF"
    override val planAnotherTrip = "Planifica un altre viatge"
    override val planAgain = "Torna a planificar amb les meves respostes"
    override val wantSomethingDifferent = "Vols alguna cosa diferent?"
    override val changePlaceholder = "Un sopar vegetarià el dia 2, i un lloc més barat per al primer dinar."
    override val updateTheRoute = "Actualitza la ruta"
    override val happyWithIt = "M'agrada així"
    override val order = "Demana: "
    override val website = "Web"
    override val openInGoogleMaps = "Obre a Google Maps"

    override fun day(day: Int) = "Dia $day"

    override fun dayAt(day: Int, stop: String) = "Dia $day · $stop"

    override fun walk(distance: String, fromStay: Boolean) = "↓ uns $distance " + if (fromStay) "des d'on t'allotges" else "des d'allà"

    override fun knownFor(stop: String) = "Què és típic de $stop"

    override fun placesCompared(count: Int) = "$count llocs comparats"

    override fun tokensUsed(count: Int) = "$count tokens utilitzats"

    override val whatItCost = "El que ha costat aquest pla"

    override fun costAbout(amount: String) = "Aquest pla ha costat uns $amount"

    override fun modelCost(model: String, read: Int, reused: Int, written: Int, amount: String?) = "$model: $read tokens llegits" + (if (reused > 0) " ($reused reutilitzats)" else "") + " i $written escrits" + (amount?.let { " · $it" } ?: "")

    override fun googleCost(searches: Int, amount: String) = "Google Places: $searches " + (if (searches == 1) "cerca" else "cerques") + " · $amount"

    override val atListPrices =
        "Als preus de llista dels proveïdors d'octubre de 2026. Google no cobra les primeres 1.000 cerques de cada mes, i la memòria cau d'un proveïdor pot abaratir els tokens llegits."

    override fun money(dollars: Double) = amount(dollars, ',') + " US$"

    override val tracing = "Traces d'OpenTelemetry (per a desenvolupadors)"

    override val tracingHint =
        "Cada execució s'envia com una traça: un span per pas, amb el que ha trigat i el que ha fallat, i res del viatge. El col·lector ha de permetre l'origen d'aquesta pàgina (CORS)."

    override val tracingAddress = "Adreça de les traces (OTLP/HTTP)"

    override val tracingHeader = "Capçalera per a la clau del servei (Nom: valor, opcional)"

    override fun ratings(count: Int) = "$count valoracions"

    override fun meal(time: MealTime) =
        when (time) {
            MealTime.BREAKFAST -> "Esmorzar"
            MealTime.LUNCH -> "Dinar"
            MealTime.SNACK -> "Berenar"
            MealTime.DINNER -> "Sopar"
        }

    override fun source(source: Source) =
        when (source) {
            Source.GUIDE -> "Guia de viatge"
            Source.MAP -> "Mapa de carrers"
            Source.REVIEWS -> "Ressenyes de Google"
        }

    override fun distance(metres: Int) = distanceIn(metres, ',')

    override val speak = "🎤 Parla"
    override val stopListening = "■ Atura"
    override val micNotAllowed = "El navegador no pot fer servir el micròfon. Permet-ho a la configuració del lloc web, o escriu."
    override val micNoNetwork = "El navegador no ha pogut connectar amb el seu servei de veu. Escriu-ho."
    override val micMissing = "No s'ha trobat cap micròfon."

    override fun micFailed(problem: String) = "El navegador no ha pogut començar a escoltar: $problem"

    override val readingYourRequest = "Llegint la teva petició"
    override val readingYourAnswers = "Llegint les teves respostes"
    override val findingYourPlaces = "Buscant els llocs del teu viatge"
    override val whereQuestion = "On vas, i quants dies?"
    override val findingDishes = "Buscant els plats típics de cada lloc"
    override val readingTheGuideFirst = "Primer, el que diu la guia de viatge de cada lloc"

    override val notingWhatYouSaid = "Anotant el que m'has dit"

    override fun seconds(count: Int) = "$count s"

    override val modelThinking = "El model pensa abans d'escriure. Aquí pot trigar fins a un minut."

    override fun whileIPlan(stop: String) = "Mentre planifico la teva ruta · $stop"
    override val anotherDish = "Un altre"

    override fun asking(question: String) = "Escrivint les preguntes: «$question»"

    override fun foundPlaces(places: List<String>) = "Trobat: ${places.joinToString(" · ")}"

    override fun namingDishes(dishes: List<String>) = "Plats: ${dishes.joinToString(", ")}…"

    override fun notOnTheMap(places: List<String>) = "No he trobat ${places.joinToString(" ni ")} al mapa. Quin lloc vols dir?"

    override fun findingOnTheMap(place: String) = "Buscant $place al mapa"

    override fun readingTheGuide(page: String) = "Llegint la guia de viatge de $page"

    override fun lookingOnTheMap(cuisine: String, stop: String) = "Buscant restaurants de cuina local al mapa de $stop"

    override fun readingReviews(stop: String) = "Llegint ressenyes de $stop"

    override fun searchingTheMap(stop: String, what: String) = "Buscant $what al mapa de $stop"

    override fun searchingReviews(stop: String, query: String) = "Buscant $query a les ressenyes de $stop"

    override val theGuide = "La guia de viatge"
    override val theMap = "El mapa de carrers"
    override val theReviews = "Les ressenyes de Google"

    override fun tookTooLong(source: String, stop: String) = "$source ha trigat massa per a $stop."

    override fun gaveNoAnswer(source: String, stop: String, problem: String) = "$source no ha respost per a $stop: $problem"

    override fun stayNotOnTheMap(place: String, stop: String) =
        "No he trobat «$place» de $stop al mapa, així que cap àpat no s'ha triat per ser a prop d'on dorms. Demana un canvi amb un carrer o una plaça propers."

    override fun nothingToEat(places: List<String>, withReviews: Boolean) =
        "No he trobat cap lloc per menjar a ${places.joinToString(" ni ")}, així que encara no hi ha ruta. Torna-ho a provar més tard, quan les fonts responguin" +
            (if (withReviews) "" else ", o afegeix una clau de Google a Configuració") + ". També pots demanar un canvi, com ara un altre poble proper."

    override fun noNewPlace(meals: List<String>) =
        (if (meals.size == 1) "No he trobat cap altre lloc per a aquest àpat: " else "No he trobat cap altre lloc per a aquests àpats: ") +
            meals.joinToString("; ") + ". Torna a demanar el canvi, o digues què et ve de gust."

    override fun placesOffered(meals: List<String>) =
        "Et proposo uns quants llocs per a " + meals.joinToString("; ") + ". El primer és a la ruta fins que toquis el que vulguis."

    override val routeReady = "La teva ruta és a punt. Si alguna cosa no et fa el pes, demana un canvi."

    override fun notConfirmed(food: String, places: List<String>) = "Cap font confirma que aquests llocs serveixin menjar \"$food\": ${places.joinedWith("i")}. Comprova-ho abans d'anar-hi."

    override val howMuchTime = Question(Topic.WHERE, "Quant de temps tens per explorar?", listOf("Una tarda", "Un dia", "Un cap de setmana", "Uns quants dies"))

    override val home = "Casa"

    override val youLiveHere = "Vius aquí"

    override val staySaidNowhere = "Vols alguns àpats a prop d'on dorms, però no sé on és. Demana un canvi amb un carrer o una plaça propers."

    override val sampleRequest = "Dos dies a Lisboa. M'encanten el marisc i els dolços."
    override val sampleStop = "Lisboa"
    override val sampleWishes = "marisc i dolços"
    override val sampleQuestions =
        listOf(
            Question(
                Topic.ALLERGIES,
                "A Lisboa es cuina molt de marisc, i molts dels seus dolços porten ametlla. Tens alguna al·lèrgia alimentària?",
                listOf("Cap", "Marisc", "Fruits secs", "Gluten"),
                alone = listOf("Cap"),
            ),
            Question(Topic.DIET, "Lisboa estima el porc tant com el peix: la bifana és un entrepà de porc. Hi ha res que no mengis?", listOf("Menjo de tot", "Sense porc", "Piscivegetarià", "Vegetarià"), alone = listOf("Menjo de tot")),
            Question(Topic.DATES, "Quan hi vas? Al juny tota la ciutat fa sardines a la brasa al carrer.", listOf("Al juny", "Un altre mes d'estiu", "Una altra època de l'any")),
        )
    override val sampleDishes =
        listOf(
            "Bacallà esmicolat amb ceba, patata fregida molt fina i ou.",
            "Un arròs melós i caldós amb gambes, cloïsses i cranc.",
            "Sardines a la brasa, amb pa i patata bullida.",
            "Pop rostit amb all i molt d'oli d'oliva.",
            "Un panet amb porc a làmines fines cuit amb all i vi blanc.",
            "Una tarteleta de crema amb la superfície torrada, que es menja calenta i amb canyella.",
            "Un licor de guinda que es pren en un gotet, dret a la barra.",
        )
    override val sampleStories =
        listOf(
            "Diuen que porta el nom d'en Brás, que tenia una taverna al Bairro Alto.",
            "Arriba a taula a la seva olla, per compartir entre dos o més, i es menja amb cullera.",
            "Al juny, per les festes de Sant Antoni, tota la ciutat les fa a la brasa al carrer.",
            "El lagareiro treballava al molí d'oli: per això el pop neda en oli d'oliva.",
            "A Lisboa hi posen mostassa i se la mengen dempeus a la barra, amb una canya petita.",
            "Els primers els van fer els monjos del Monestir dels Jerònims, a Belém. Quan van tancar els monestirs, el 1834, una pastisseria del costat en va comprar la recepta.",
            "Demana-la com elas, amb les guindes al got, o sem elas, sense.",
        )
    override val sampleFirstSearch = "Primer, un lloc per als pastéis de nata."
    override val sampleWhy = "Exemple: triat pel que en diuen les fonts, no per aquest plat."
    override val sampleWhyPastry = "Exemple: la primera pastisseria que ha tornat el mapa de carrers."
    override val sampleSummary =
        "Aquest és el viatge d'exemple. L'ha escrit un guió, no un model: els restaurants són reals i surten de la guia de viatge i del mapa de carrers, " +
            "però el guió ha agafat els que tenen més suport sense llegir què cuinen. " +
            "Afegeix la clau d'un model a Configuració i el planificador triarà cada lloc per un plat, per al teu propi viatge."
    override val sampleNoChanges = "L'exemple és un guió i no pot canviar la ruta. Afegeix la clau d'un model a Configuració i el planificador seguirà el que li demanis."

    override val stepSpecialists = "Els llocs coneguts per cada plat"
    override fun lookingForSpecialists(stop: String) = "Buscant els llocs coneguts pels plats de $stop"
    override fun knownForDish(dish: String) = "Conegut per: $dish"
    override fun booking(reservation: String) =
        when (reservation) {
            "required" -> "Cal reservar"
            "recommended" -> "Millor reservar"
            "yes" -> "Accepta reserves"
            "no" -> "Sense reserves: hi vas directament"
            else -> null
        }
    override val thisDayInGoogleMaps = "Aquest dia a Google Maps"
    override val moreActions = "Més"
    override val readMore = "Llegeix-ne més"
    override val readLess = "Mostra'n menys"
    override val onTheMap = "Mostra-ho al mapa"
    override val saveForMyMaps = "Desa els llocs per a Google Maps"
    override val myMapsHintBefore = "Per tenir-los tots a Google Maps: a "
    override val myMapsHintAfter =
        ", crea un mapa nou, tria Importa i escull el fitxer. Després el mapa surt a l'app de Google Maps, a Desats, Mapes. " +
            "És més fàcil importar-lo des d'un ordinador."
    override val saveOneByOne = "Desa els llocs a Google Maps, un per un"
    override val oneByOneHint =
        "Cada botó obre el lloc a Google Maps. Allà toca Desa, tria una llista, per exemple una de nova per a aquest viatge, i torna per al següent. " +
            "La pàgina recorda quins has obert."
    override val saveInGoogleMaps = "Desa a Google Maps"
    override fun openedOfAll(opened: Int, all: Int) = "$opened de $all oberts"
    override val sendTheFile = "Envia't el fitxer"
    override val sentFileText =
        "Els llocs de la meva Foodie Route. En un ordinador, obre Google My Maps, crea un mapa nou i importa aquest fitxer. " +
            "Si et pregunta quines columnes fer servir, tria Latitude i Longitude per al lloc, i Name per al títol."
    override val saveToFile = "Desa el pla en un fitxer"
    override val openAFile = "Obre un pla desat"
    override val savedFileHint =
        "El fitxer té el teu viatge, el que li vas dir al planificador i tot el que va trobar, però cap de les teves claus. " +
            "El pots tornar a obrir aquí més endavant, o compartir-lo per mostrar com va triar el planificador."
    override val notAPlanFile = "Aquest fitxer no és un pla de Foodie Route."
    override val planFromANewerPage = "Una versió més nova de la pàgina va desar aquest pla. Torna a carregar la pàgina i obre'l de nou."

    override val changeThisPlace = "Canvia aquest lloc"
    override val whyNotThisOne = "Per què no aquest?"

    override fun swapReason(reason: SwapReason) =
        when (reason) {
            SwapReason.DISH -> "No em ve de gust aquest plat"
            SwapReason.FOOD -> "Un altre tipus de menjar"
            SwapReason.CLOSER -> "Més a prop d'algun lloc"
            SwapReason.CHEAPER -> "Alguna cosa més barata"
            SwapReason.SPECIAL -> "Alguna cosa especial"
            SwapReason.TOURISTY -> "Sembla turístic"
            SwapReason.KNOWN -> "Ja el conec"
            SwapReason.CLOSED -> "Tancat o ple aquell dia"
        }
    override val whatInstead = "Què et ve de gust, en canvi?"
    override val insteadPlaceholder = "Un plat o un tipus de menjar"
    override val closerToWhat = "Més a prop de què?"
    override val closerPlaceholder = "El teu hotel, una platja, un museu…"
    override val swapNotePlaceholder = "Res més? (opcional)"
    override val findAnotherPlace = "Busca un altre lloc"

    override fun changeThesePlaces(count: Int) = "Canvia aquests $count llocs"
    override val alsoChangeAnother = "Canvia també un altre lloc"
    override val markedToChange = "Marcat per canviar"
    override val editSwap = "Edita"
    override val keepThisPlace = "Mantén-lo"
    override val changingTheRoute = "Canviant la ruta"
    override val lookingForOthers = "Buscant altres llocs…"
    override val stopChanging = "Atura"
    override val nearStay = "A prop d'on dorms"
    override val nearStayHint = "Toca els àpats que vols a prop d'on dorms."

    override fun sleepingAt(place: String) = "Dorms a $place"

    override val recommended = "Recomanat"
    override val yourChoice = "La teva tria"
    override val otherOptions = "Altres opcions"
    override val details = "Detalls"

    override fun otherOptionsCount(count: Int) = if (count == 1) "1 opció més" else "$count opcions més"
    override val chooseThisOne = "Tria aquest"

    override val shareTryOut = "Comparteix una prova amb els amics"
    override val shareTryOutHint =
        "Crea un enllaç que planifica amb aquest model i aquesta clau, protegit amb un plat secret. Digues el plat a part de l'enllaç, per exemple de paraula. Posa un límit de crèdit a la clau al seu proveïdor i esborra-la allà quan acabi la prova: fins aleshores, qualsevol amb l'enllaç i el plat la pot fer servir."
    override val secretDish = "Plat secret"
    override val secretDishPlaceholder = "Per exemple, pastel de nata"
    override val tryOutLasts = "La pàgina oblida la clau després de"

    override fun days(count: Int) = if (count == 1) "1 dia" else "$count dies"
    override val makeLink = "Crea l'enllaç"
    override val copyLink = "Copia"
    override val linkCopied = "Copiat."
    override val shareLink = "Comparteix"
    override val linkMessage = "Prova Foodie Route: planifica on menjar en un viatge. El plat secret te'l dic a part."
    override val friendShared = "T'han compartit Foodie Route"
    override val enterSecretDish = "Escriu el plat secret que t'han dit i planifica els teus viatges amb el seu model."
    override val openTryOut = "Obre"
    override val opening = "Obrint…"
    override val wrongDish = "Aquest no és el plat, o l'enllaç està tallat. Torna-ho a preguntar."

    override fun friendsModelUntil(date: String) = "Planifiques amb el model que t'han compartit fins al $date."
    override val tryOutEnded = "La prova que et van compartir s'ha acabat. Per continuar planificant, afegeix una clau pròpia a Configuració."

    override val openRouterModels = "Models d'OpenRouter que poden planificar, amb el que costa un milió de tokens, llegits / escrits"
    override val loadingModels = "Carregant els models d'OpenRouter…"
    override val chooseAModel = "Tria un model"
    override val shareGoogleKey = "Comparteix també la clau de Google Places"
    override val shareGoogleKeyHint =
        "Així els teus amics també veuen valoracions, i cada cerca te la cobren a tu. A la consola de Google Cloud pots abaixar la quota diària de Places API (New), perquè la clau s'aturi després d'aquestes cerques al dia."
}
